import React, { useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useAdminAuth } from '../context/AdminAuthContext';

// ─── Styles ────────────────────────────────────────────────────────────────────

const Wrapper = styled.div`
  min-height: 100vh;
  background: #0d0d0d;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 24px;
`;

const Card = styled.div`
  width: 100%;
  max-width: 420px;
  background: #1a1a1a;
  border: 1px solid rgba(255,255,255,0.08);
  border-radius: 16px;
  padding: 48px 40px;
`;

const Logo = styled.div`
  text-align: center;
  margin-bottom: 36px;

  h1 {
    font-size: 1.5rem;
    font-weight: 800;
    color: #fff;
    margin: 0 0 6px;
    letter-spacing: -0.5px;
  }

  p {
    font-size: 0.85rem;
    color: rgba(255,255,255,0.4);
    margin: 0;
  }

  span {
    color: #fe88f1;
  }
`;

const FieldLabel = styled.label`
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: rgba(255,255,255,0.45);
  margin-bottom: 8px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 16px;
  background: rgba(255,255,255,0.05);
  border: 1.5px solid rgba(255,255,255,0.1);
  border-radius: 8px;
  color: #fff;
  font-size: 0.95rem;
  font-family: inherit;
  box-sizing: border-box;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: rgba(254,136,241,0.5);
  }
  &::placeholder { color: rgba(255,255,255,0.2); }
`;

const FieldGroup = styled.div`
  margin-bottom: 20px;
`;

const LoginBtn = styled.button`
  width: 100%;
  padding: 14px;
  background: #fe88f1;
  color: #0d0d0d;
  border: none;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 700;
  cursor: pointer;
  margin-top: 8px;
  transition: opacity 0.2s, transform 0.15s;

  &:hover:not(:disabled) {
    opacity: 0.88;
    transform: translateY(-1px);
  }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const ErrorMsg = styled.p`
  font-size: 0.85rem;
  color: #ff7e7e;
  text-align: center;
  margin: 16px 0 0;
`;

const Badge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 28px;
`;

const Lock = styled.div`
  width: 52px;
  height: 52px;
  background: rgba(254,136,241,0.12);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
`;

// ─── Component ─────────────────────────────────────────────────────────────────

const AdminLogin = () => {
  const { login } = useAdminAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(email, password);
      navigate('/admin/dashboard');
    } catch (err) {
      setError(err.message || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Wrapper>
      <Helmet>
        <title>Admin Login — MyCinemaDB</title>
      </Helmet>

      <Card>
        <Badge>
          <Lock>🔐</Lock>
        </Badge>

        <Logo>
          <h1>My<span>Cinema</span>DB</h1>
          <p>Admin access only</p>
        </Logo>

        <form onSubmit={handleSubmit}>
          <FieldGroup>
            <FieldLabel htmlFor="admin-email">Email</FieldLabel>
            <Input
              id="admin-email"
              type="email"
              placeholder="admin@mycinemadb.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </FieldGroup>

          <FieldGroup>
            <FieldLabel htmlFor="admin-password">Password</FieldLabel>
            <Input
              id="admin-password"
              type="password"
              placeholder="••••••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
            />
          </FieldGroup>

          <LoginBtn type="submit" disabled={loading}>
            {loading ? 'Signing in…' : 'Sign In'}
          </LoginBtn>

          {error && <ErrorMsg>{error}</ErrorMsg>}
        </form>
      </Card>
    </Wrapper>
  );
};

export default AdminLogin;
